import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Skeleton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { MainLayout } from '../../layouts/MainLayout';
import {
  projectService,
  type ProjectWithCreator,
  type ProjectStats,
  type AdminProjectFilters,
  MA_METHOD_LABELS,
} from '../../services/api/projectService';
import { userService } from '../../services/api/userService';
import type { MAMethod, ProjectStatus, User } from '../../types';

const statusConfig: Record<ProjectStatus, { label: string; color: 'info' | 'warning' | 'success' | 'default' | 'error' }> = {
  planning: { label: '検討中', color: 'info' },
  in_progress: { label: '進行中', color: 'warning' },
  completed: { label: '完了', color: 'success' },
  cancelled: { label: '中止', color: 'default' },
};

export function AllProjectsPage() {
  const navigate = useNavigate();

  // データ状態
  const [projects, setProjects] = useState<ProjectWithCreator[]>([]);
  const [stats, setStats] = useState<ProjectStats | null>(null);
  const [users, setUsers] = useState<User[]>([]);

  // ローディング状態
  const [isLoading, setIsLoading] = useState(true);
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  const [isUsersLoading, setIsUsersLoading] = useState(true);

  // フィルター状態
  const [searchQuery, setSearchQuery] = useState('');
  const [maMethodFilter, setMaMethodFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [creatorFilter, setCreatorFilter] = useState<string>('');

  // ダイアログ状態
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectWithCreator | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // メッセージ状態
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 案件一覧を取得
  const fetchProjects = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const filters: AdminProjectFilters = {};
      if (searchQuery) filters.keyword = searchQuery;
      if (maMethodFilter) filters.maMethod = maMethodFilter as MAMethod;
      if (statusFilter) filters.status = statusFilter as ProjectStatus;
      if (creatorFilter) filters.creatorId = creatorFilter;

      const data = await projectService.getAllProjects(filters);
      setProjects(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '案件の取得に失敗しました';
      setError(errorMessage);
      setProjects([]);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, maMethodFilter, statusFilter, creatorFilter]);

  // 統計を取得
  const fetchStats = useCallback(async () => {
    setIsStatsLoading(true);
    try {
      const data = await projectService.getProjectStats();
      setStats(data);
    } catch (err) {
      // 統計取得失敗はエラー表示せず、フォールバック値を使用
      console.error('統計取得エラー:', err);
      setStats(null);
    } finally {
      setIsStatsLoading(false);
    }
  }, []);

  // ユーザー一覧を取得（フィルター用）
  const fetchUsers = useCallback(async () => {
    setIsUsersLoading(true);
    try {
      const data = await userService.getUsers();
      setUsers(data);
    } catch (err) {
      // ユーザー取得失敗はエラー表示せず、空配列を使用
      console.error('ユーザー取得エラー:', err);
      setUsers([]);
    } finally {
      setIsUsersLoading(false);
    }
  }, []);

  // 初期データ読み込み
  useEffect(() => {
    fetchStats();
    fetchUsers();
  }, [fetchStats, fetchUsers]);

  // フィルター変更時にデータを再取得
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // 全データをリフレッシュ
  const handleRefresh = () => {
    fetchProjects();
    fetchStats();
  };

  const handleViewProject = (projectId: string) => {
    navigate(`/projects/${projectId}`);
  };

  const handleOpenDeleteDialog = (project: ProjectWithCreator) => {
    setSelectedProject(project);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedProject(null);
  };

  const handleDelete = async () => {
    if (!selectedProject) return;

    setIsDeleting(true);
    try {
      await projectService.adminDeleteProject(selectedProject.id);
      setMessage({ type: 'success', text: `${selectedProject.name} を削除しました` });
      // データを再取得
      fetchProjects();
      fetchStats();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '削除に失敗しました';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsDeleting(false);
      handleCloseDeleteDialog();
    }
  };

  // 統計のフォールバック値を計算（API取得失敗時）
  const displayStats = stats ?? {
    total: projects.length,
    byStatus: {
      planning: projects.filter((p) => p.status === 'planning').length,
      in_progress: projects.filter((p) => p.status === 'in_progress').length,
      completed: projects.filter((p) => p.status === 'completed').length,
      cancelled: projects.filter((p) => p.status === 'cancelled').length,
    },
    byMethod: {
      stock_transfer: projects.filter((p) => p.maMethod === 'stock_transfer').length,
      business_transfer: projects.filter((p) => p.maMethod === 'business_transfer').length,
      merger: projects.filter((p) => p.maMethod === 'merger').length,
      company_split: projects.filter((p) => p.maMethod === 'company_split').length,
      stock_exchange: projects.filter((p) => p.maMethod === 'stock_exchange').length,
    },
  };

  return (
    <MainLayout>
      <Box>
        {/* Page Title */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1" fontWeight={500}>
            全案件管理
          </Typography>
          <IconButton onClick={handleRefresh} disabled={isLoading} title="更新">
            <RefreshIcon />
          </IconButton>
        </Box>

        {/* Message */}
        {message && (
          <Alert
            severity={message.type}
            sx={{ mb: 3 }}
            onClose={() => setMessage(null)}
          >
            {message.text}
          </Alert>
        )}

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Stats Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: 2,
            mb: 3,
          }}
        >
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                全案件数
              </Typography>
              {isStatsLoading ? (
                <Skeleton variant="text" width={60} height={40} />
              ) : (
                <Typography variant="h4" color="text.primary">
                  {displayStats.total}
                </Typography>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                検討中
              </Typography>
              {isStatsLoading ? (
                <Skeleton variant="text" width={60} height={40} />
              ) : (
                <Typography variant="h4" color="info.main">
                  {displayStats.byStatus.planning}
                </Typography>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                進行中
              </Typography>
              {isStatsLoading ? (
                <Skeleton variant="text" width={60} height={40} />
              ) : (
                <Typography variant="h4" color="warning.main">
                  {displayStats.byStatus.in_progress}
                </Typography>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                完了
              </Typography>
              {isStatsLoading ? (
                <Skeleton variant="text" width={60} height={40} />
              ) : (
                <Typography variant="h4" color="success.main">
                  {displayStats.byStatus.completed}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Filter Bar */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              placeholder="案件名・対象企業名で検索"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ flex: 1, minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>M&A手法</InputLabel>
              <Select
                value={maMethodFilter}
                label="M&A手法"
                onChange={(e) => setMaMethodFilter(e.target.value)}
              >
                <MenuItem value="">すべて</MenuItem>
                <MenuItem value="stock_transfer">株式譲渡</MenuItem>
                <MenuItem value="business_transfer">事業譲渡</MenuItem>
                <MenuItem value="merger">合併</MenuItem>
                <MenuItem value="company_split">会社分割</MenuItem>
                <MenuItem value="stock_exchange">株式交換・移転</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>ステータス</InputLabel>
              <Select
                value={statusFilter}
                label="ステータス"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">すべて</MenuItem>
                <MenuItem value="planning">検討中</MenuItem>
                <MenuItem value="in_progress">進行中</MenuItem>
                <MenuItem value="completed">完了</MenuItem>
                <MenuItem value="cancelled">中止</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>作成者</InputLabel>
              <Select
                value={creatorFilter}
                label="作成者"
                onChange={(e) => setCreatorFilter(e.target.value)}
                disabled={isUsersLoading}
              >
                <MenuItem value="">すべて</MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Paper>

        {/* Projects Table */}
        <Paper elevation={1}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 500 }}>案件名</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>対象企業名</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>M&A手法</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>ステータス</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>作成者</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>作成日</TableCell>
                  <TableCell sx={{ fontWeight: 500 }} align="center">
                    操作
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  // ローディング中のスケルトン表示
                  Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell><Skeleton variant="text" /></TableCell>
                      <TableCell><Skeleton variant="text" /></TableCell>
                      <TableCell><Skeleton variant="text" /></TableCell>
                      <TableCell><Skeleton variant="rounded" width={80} height={24} /></TableCell>
                      <TableCell><Skeleton variant="text" /></TableCell>
                      <TableCell><Skeleton variant="text" /></TableCell>
                      <TableCell align="center">
                        <Skeleton variant="circular" width={32} height={32} sx={{ display: 'inline-block', mr: 1 }} />
                        <Skeleton variant="circular" width={32} height={32} sx={{ display: 'inline-block' }} />
                      </TableCell>
                    </TableRow>
                  ))
                ) : projects.length > 0 ? (
                  projects.map((project) => (
                    <TableRow key={project.id} hover>
                      <TableCell>{project.name}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>
                        {project.targetCompany}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>
                        {MA_METHOD_LABELS[project.maMethod]}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={statusConfig[project.status].label}
                          color={statusConfig[project.status].color}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>
                        {project.creator?.name ?? '不明'}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>
                        {new Date(project.createdAt).toLocaleDateString('ja-JP')}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleViewProject(project.id)}
                          title="詳細を見る"
                        >
                          <ViewIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenDeleteDialog(project)}
                          title="削除"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">
                        該当する案件が見つかりません
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>案件削除の確認</DialogTitle>
        <DialogContent>
          <Typography>
            「{selectedProject?.name}」を削除してもよろしいですか？
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            この操作は取り消せません。関連するすべてのチェック結果も削除されます。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={isDeleting}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? <CircularProgress size={24} /> : '削除'}
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
}
